import type { EventDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { useArchiveEvent, useHardDeleteEvent, usePublishEvent, useRestoreEvent } from '../mutations';

interface Props {
  event: EventDto;
  isSuperadmin: boolean;
  onDeleted: () => void;
}

export function EventStatusActions({ event, isSuperadmin, onDeleted }: Props) {
  const publish = usePublishEvent(event.id);
  const archive = useArchiveEvent(event.id);
  const restore = useRestoreEvent(event.id);
  const hardDelete = useHardDeleteEvent();
  const [confirmSlug, setConfirmSlug] = useState('');

  const handle = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось выполнить действие');
    }
  };

  const onHardDelete = async () => {
    try {
      await hardDelete.mutateAsync(event.id);
      toast.success('Событие удалено');
      onDeleted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось удалить');
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {event.status === 'DRAFT' && (
        <Button
          onClick={() => handle(() => publish.mutateAsync(), 'Опубликовано')}
          disabled={publish.isPending}
        >
          Опубликовать
        </Button>
      )}
      {event.status === 'PUBLISHED' && (
        <Button
          variant="outline"
          onClick={() => handle(() => archive.mutateAsync(), 'Архивировано')}
          disabled={archive.isPending}
        >
          Архивировать
        </Button>
      )}
      {event.status === 'ARCHIVED' && (
        <Button
          variant="outline"
          onClick={() => handle(() => restore.mutateAsync(), 'Восстановлено как черновик')}
          disabled={restore.isPending}
        >
          Восстановить в черновик
        </Button>
      )}
      {isSuperadmin && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Удалить навсегда</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить «{event.title}» безвозвратно?</AlertDialogTitle>
              <AlertDialogDescription>
                Будут удалены все RSVP к этому событию. Действие нельзя отменить.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="my-4">
              <p className="text-sm mb-2">
                Подтвердите, введя <code className="bg-muted px-1 rounded">delete</code>:
              </p>
              <Input
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                placeholder="delete"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmSlug('')}>Отмена</AlertDialogCancel>
              <AlertDialogAction disabled={confirmSlug !== 'delete'} onClick={onHardDelete}>
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
